import React, { forwardRef } from "react";

import { classNames } from "../../utils/common";
import { getTransformedPaddingWithCSSVariables } from "../utils";

import type { Gap, LayoutAriaAttributes, Padding } from "../types";
import type { CatalystColors } from "../../constants/styles";

import styles from "./Grid.module.css";

// Layout-specific roles that are allowed
// Roles like list, listitem, presentation, rowgroup can be later when need be
type AllowedGridRoles = "grid" | "row" | "cell" | "group";

// Grid-specific ARIA attributes
type GridAriaAttributes = {
  "aria-rowcount"?: number;
  "aria-colcount"?: number;
  "aria-rowindex"?: number;
  "aria-colindex"?: number;
};

type GridProps = {
  /**
   * The tag of the grid
   * @default "div"
   **/
  as?: "div";
  /**
   * A custom class name to apply to the grid.
   * Use this with caution, might override the default properties of the grid.
   **/
  dangerouslySetClassName?: string;
  /**
   * The padding of the grid
   **/
  padding?: Padding;
  /**
   * The children of the grid
   **/
  children: React.ReactNode;
  /**
   * The justify content of the grid based on main axis
   **/
  justify?: React.CSSProperties["justifyItems"];
  /**
   * The align items of the grid based on cross axis
   **/
  align?: React.CSSProperties["alignItems"];
  /**
   * Gap between the rows
   */
  rowGap?: Gap;
  /**
   * Gap between the columns
   */
  columnGap?: Gap;
  /**
   * The number of rows in the grid
   */
  rows?: number;
  /**
   * The background color of the grid
   **/
  backgroundColor?: CatalystColors;
  /**
   * The width of the grid container
   */
  width?:
    | `${string}rem`
    | `${string}%`
    | "fit-content"
    | "max-content"
    | "min-content";
  /**
   * The ARIA role for the grid container
   * Only layout-specific roles are allowed
   */
  role?: AllowedGridRoles;
} & GridColumnsProps &
  LayoutAriaAttributes &
  GridAriaAttributes;

type GridColumnsProps =
  | {
      /**
       * The number of columns in the grid
       */
      columns?: number;
      /**
       * The minimum length of the grid column
       * The length value representing x in minmax(min(x, 100%), 1fr)
       * This is not considered when columns are provided
       */
      minColWidth: `${string}rem` | `${string}%`;
    }
  | {
      columns: number;
      minColWidth?: `${string}rem` | `${string}%`;
    };

export const Grid = forwardRef<HTMLElement, GridProps>((props, ref) => {
  const {
    as = "div",
    children,
    justify,
    align,
    rowGap,
    columnGap,
    rows,
    columns,
    minColWidth,
    padding,
    backgroundColor,
    width,
    role,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-details": ariaDetails,
    "aria-hidden": isAriaHidden,
    "aria-busy": isAriaBusy,
    "aria-disabled": isAriaDisabled,
    "aria-rowcount": ariaRowCount,
    "aria-colcount": ariaColCount,
    "aria-rowindex": ariaRowIndex,
    "aria-colindex": ariaColIndex,
    dangerouslySetClassName = ""
  } = props;

  return (
    <>
      {React.createElement(as, {
        children,
        ref,
        role,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        "aria-details": ariaDetails,
        "aria-hidden": isAriaHidden,
        "aria-busy": isAriaBusy,
        "aria-disabled": isAriaDisabled,
        "aria-rowcount": ariaRowCount,
        "aria-colcount": ariaColCount,
        "aria-rowindex": ariaRowIndex,
        "aria-colindex": ariaColIndex,
        style: {
          rowGap: rowGap ? `var(--space-${rowGap})` : undefined,
          columnGap: columnGap ? `var(--space-${columnGap})` : undefined,
          gridTemplateRows: rows ? `repeat(${rows}, auto)` : undefined,
          gridTemplateColumns: columns
            ? `repeat(${columns},  1fr)`
            : `repeat(auto-fit, minmax(min(${minColWidth}, 100%), 1fr))`,
          justifyItems: justify,
          alignItems: align,
          padding: getTransformedPaddingWithCSSVariables(padding),
          backgroundColor,
          width
        },
        className: classNames({
          [styles.catalystGridContainer]: true,
          [dangerouslySetClassName]: !!dangerouslySetClassName
        })
      })}
    </>
  );
});
